import { Box, Button, Container, Grid, Paper, Typography } from "@material-ui/core";
import CameraAltIcon from "@material-ui/icons/CameraAlt";
import EmailIcon from "@material-ui/icons/Email";
import LockIcon from "@material-ui/icons/Lock";
import PersonIcon from "@material-ui/icons/Person";
import PhoneIcon from "@material-ui/icons/Phone";
import SaveIcon from "@material-ui/icons/Save";
import SchoolIcon from "@material-ui/icons/School";
import VerifiedUserIcon from "@material-ui/icons/VerifiedUser";
import authApi from "api/authApi";
import { useAppDispatch, useAppSelector } from "app/hooks";
import NO_AVATAR from "assets/img/no_avatar.png";
import InputField from "components/FormFields/InputField";
import { SelectField } from "components/FormFields/SelectFiled";
import { fetchInfoLogin, selectInfoLogin } from "features/Auth/authSlice";
import { fetchDataOptions, selectListClass } from "features/Auth/options/optionsSlice";
import { InfoUser } from "models/auth";
import { ResponseStatus } from "models/common";
import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import ChangePassword from "./ChangePassword";
import useStyles from "./style";

export default function Profile() {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const infoLogin = useAppSelector(selectInfoLogin);
  const [disabledSubmit, setDisabledSubmit] = useState(false);
  const listClass = useAppSelector(selectListClass);

  useEffect(() => {
    dispatch(fetchDataOptions());
  }, [dispatch]);

  const initialValues = {
    name: infoLogin.name || "",
    email: infoLogin.email || "",
    phone: infoLogin.phone || "",
    classId: infoLogin?.class?.id || "",
  };

  const { control, handleSubmit, reset } = useForm({
    defaultValues: useMemo(() => initialValues, [initialValues]),
  });

  useEffect(() => {
    reset(initialValues);
  }, [reset, infoLogin]);

  const handleSubmitForm = async (value: any) => {
    setDisabledSubmit(true);
    try {
      const res: InfoUser = await authApi.updateInfo(value, infoLogin.id);
      setDisabledSubmit(false);
      if (res.responseCode === "00") {
        toast.success(`${t("auth.edit.success") || "Cập nhật thông tin thành công!"}`);
        dispatch(fetchInfoLogin());
      } else {
        setDisabledSubmit(false);
        throw new Error(res.responseDescription);
      }
    } catch (error: any) {
      setDisabledSubmit(false);
      toast.error(error.message || "Cập nhật thất bại, vui lòng thử lại.");
    }
  };

  const handleEditAvatar = async (event: any) => {
    const fileAvatar = event?.target?.files?.[0];
    if (!fileAvatar) return;
    const formData = new FormData();
    formData.append("image", fileAvatar);
    try {
      const res: ResponseStatus = await authApi.updateImage(formData);
      if (res.responseCode === "00") {
        toast.success("Cập nhật ảnh đại diện thành công!");
        dispatch(fetchInfoLogin());
      }
    } catch (error) {
      console.log(error);
      toast.error("Cập nhật ảnh đại diện thất bại.");
    }
  };

  return (
    <Box className={classes.background}>
      <Container className={classes.container}>
        {/* Header Banner */}
        <Box className={classes.headerBanner}>
          <Box className={classes.headerGlow} />
          <Box className={classes.headerBadge}>
            <PersonIcon style={{ fontSize: 16 }} />
            THÔNG TIN TÀI KHOẢN
          </Box>
          <Typography className={classes.headerTitle}>
            HỒ SƠ CÁ NHÂN HỌC VIÊN
          </Typography>
          <Typography className={classes.headerSubtitle}>
            Quản lý và cập nhật thông tin cá nhân, liên hệ và mật khẩu bảo mật tài khoản trên hệ thống đào tạo GPET Edu.
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {/* Left Column: Avatar & Quick Info */}
          <Grid item xs={12} md={4}>
            <Paper className={classes.profileCard} elevation={0}>
              <Box className={classes.avatarWrapper}>
                <Box className={classes.avatarBox}>
                  <img src={infoLogin?.profileImageUrl || NO_AVATAR} alt="Avatar" />
                  <label htmlFor="contained-button-file" className={classes.uploadAvatarBtn}>
                    <CameraAltIcon style={{ fontSize: 20 }} />
                  </label>
                  <input
                    accept="image/*"
                    id="contained-button-file"
                    type="file"
                    name="image"
                    onChange={handleEditAvatar}
                    style={{ display: "none" }}
                  />
                </Box>

                <Typography className={classes.userName}>
                  {infoLogin?.name || "Học Viên GPET"}
                </Typography>
                <Typography className={classes.userEmail}>
                  {infoLogin?.email || infoLogin?.phone || "gpet-student@edu.vn"}
                </Typography>

                <Box className={classes.userRoleBadge}>
                  <VerifiedUserIcon style={{ fontSize: 16 }} />
                  HỌC VIÊN CHÍNH THỨC
                </Box>
              </Box>

              <Box style={{ backgroundColor: "#F8FAFC", borderRadius: 16, padding: 18, border: "1px solid #E2E8F0" }}>
                <Box display="flex" justifyContent="space-between" mb={1.5}>
                  <Typography style={{ fontSize: "0.85rem", color: "#64748B", fontWeight: 700 }}>
                    MÃ HỌC VIÊN
                  </Typography>
                  <Typography style={{ fontSize: "0.85rem", color: "#0F172A", fontWeight: 800 }}>
                    #{infoLogin?.id || "386"}
                  </Typography>
                </Box>

                <Box display="flex" justifyContent="space-between" mb={1.5}>
                  <Typography style={{ fontSize: "0.85rem", color: "#64748B", fontWeight: 700 }}>
                    LỚP ĐÀO TẠO
                  </Typography>
                  <Typography style={{ fontSize: "0.85rem", color: "#4F46E5", fontWeight: 800 }}>
                    {infoLogin?.class?.name || "Lớp GPB 2026"}
                  </Typography>
                </Box>

                <Box display="flex" justifyContent="space-between">
                  <Typography style={{ fontSize: "0.85rem", color: "#64748B", fontWeight: 700 }}>
                    TRẠNG THÁI
                  </Typography>
                  <Typography style={{ fontSize: "0.85rem", color: "#10B981", fontWeight: 800 }}>
                    Đang hoạt động
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Right Column: Edit Profile & Change Password */}
          <Grid item xs={12} md={8}>
            {/* Personal Info Card */}
            <Paper className={classes.profileCard} elevation={0}>
              <Box className={classes.sectionHeader}>
                <PersonIcon style={{ fontSize: 24, color: "#4F46E5" }} />
                <Typography className={classes.sectionTitle}>
                  {t("profile.thongtincanhan") || "THÔNG TIN CÁ NHÂN"}
                </Typography>
              </Box>

              <form onSubmit={handleSubmit(handleSubmitForm)} className={classes.formContainer}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Box className={classes.fieldGroup}>
                      <Typography className={classes.formLabel}>
                        <PersonIcon style={{ fontSize: 18, color: "#4F46E5" }} />
                        {t("auth.fullName") || "HỌ VÀ TÊN"}
                      </Typography>
                      <InputField name="name" label={t("auth.fullName")} control={control} borderRadius="12px" />
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box className={classes.fieldGroup}>
                      <Typography className={classes.formLabel}>
                        <EmailIcon style={{ fontSize: 18, color: "#4F46E5" }} />
                        {t("auth.email") || "EMAIL TÀI KHOẢN"}
                      </Typography>
                      <InputField name="email" label={t("auth.email")} control={control} disabled borderRadius="12px" />
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box className={classes.fieldGroup}>
                      <Typography className={classes.formLabel}>
                        <PhoneIcon style={{ fontSize: 18, color: "#4F46E5" }} />
                        {t("auth.phone") || "SỐ ĐIỆN THOẠI"}
                      </Typography>
                      <InputField name="phone" label={t("auth.phone")} control={control} borderRadius="12px" />
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box className={classes.fieldGroup}>
                      <Typography className={classes.formLabel}>
                        <SchoolIcon style={{ fontSize: 18, color: "#4F46E5" }} />
                        {t("auth.class") || "LỚP HỌC"}
                      </Typography>
                      <SelectField
                        name="classId"
                        label={`${t("auth.class")}`}
                        control={control}
                        borderRadius="12px"
                        options={listClass}
                      />
                    </Box>
                  </Grid>
                </Grid>

                <Box textAlign="right" mt={2}>
                  <Button
                    disabled={disabledSubmit}
                    type="submit"
                    variant="contained"
                    startIcon={<SaveIcon />}
                    className={classes.btnSubmit}
                  >
                    {t("auth.button.updateprofile") || "LƯU THÔNG TIN CÁ NHÂN"}
                  </Button>
                </Box>
              </form>
            </Paper>

            {/* Change Password Card */}
            <Paper className={classes.profileCard} elevation={0}>
              <Box className={classes.sectionHeader}>
                <LockIcon style={{ fontSize: 24, color: "#4F46E5" }} />
                <Typography className={classes.sectionTitle}>
                  {t("profile.thaydoimatkhau") || "ĐỔI MẬT KHẨU BẢO MẬT"}
                </Typography>
              </Box>

              <ChangePassword />
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
